type TColorElementType =
  | 'avatar-background'
  | 'video-border'
  | 'video-background'
  | 'hand-raise-badge'
  | 'voice-indicator'
  | 'wavy-background'
  | 'wavy-color';

type TColorPreset = { saturation: number; lightness: number; opacity?: number };

interface THslOptions {
  hueColor: number;
  colorPreset?: TColorPreset;
}

/**
 * Mapping of element types to their corresponding saturation and lightness values.
 * Modify these values to adjust colors across the entire application.
 */
const COLOR_PRESETS: Record<TColorElementType, TColorPreset> = {
  'avatar-background': { saturation: 49, lightness: 31 },
  'video-border': { saturation: 30, lightness: 65 },
  'video-background': { saturation: 49, lightness: 31, opacity: 1 },
  'hand-raise-badge': { saturation: 60, lightness: 50 },
  'voice-indicator': { saturation: 45, lightness: 55 },
  'wavy-background': { saturation: 45, lightness: 50, opacity: 1 },
  'wavy-color': { saturation: 45, lightness: 55 },
};

const adjustColorForContrast = ({ hueColor, colorPreset = { saturation: 45, lightness: 50 } }: THslOptions): string => {
  // Return neutral gray if hue is 0 or falsy
  if (!hueColor) return `hsl(0, 0%, 50%)`;

  // Ensure hue value is within valid range (0-360)
  const clampedHue = Math.max(0, Math.min(360, hueColor));

  // Clamp saturation and lightness values to valid HSL range (0-100)
  const clampedSaturation = Math.min(100, colorPreset.saturation);
  const clampedLightness = Math.min(100, colorPreset.lightness);

  if (colorPreset.opacity) {
    return `hsla(${clampedHue}, ${clampedSaturation}%, ${clampedLightness}%, ${colorPreset.opacity})`;
  }

  // Construct and return HSL color string
  return `hsl(${clampedHue}, ${clampedSaturation}%, ${clampedLightness}%)`;
};

export const createElementColor = (hueColor: number, elementType: TColorElementType): string => {
  const colorPreset = COLOR_PRESETS[elementType];

  return adjustColorForContrast({ hueColor, colorPreset });
};
